import type { RiskReductionMeasure } from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { VariantProps } from 'class-variance-authority'
import type { badgeVariants } from '@/components/ui/badge'

export type RrmImplementationBadgeKind =
  | 'NOT_IMPLEMENTED'
  | 'IMPLEMENTED'
  | 'IMPLEMENTED_NO_EVIDENCE'
  | 'IMPLEMENTED_IN_CONFORMITY'

export function getRrmImplementationBadgeKind(
  measure: Pick<
    RiskReductionMeasure,
    | 'implementationStatus'
    | 'implementationNote'
    | 'implementationProofSources'
    | 'standardConformity'
  >
): RrmImplementationBadgeKind {
  if (measure.implementationStatus === 'NOT_IMPLEMENTED') {
    return 'NOT_IMPLEMENTED'
  }

  const hasNote =
    measure.implementationNote != null &&
    measure.implementationNote.trim().length > 0
  const hasProofSources = measure.implementationProofSources.length > 0

  if (!hasNote && !hasProofSources) {
    return 'IMPLEMENTED_NO_EVIDENCE'
  }

  const hasConformityStandards =
    (measure.standardConformity?.standards.length ?? 0) > 0

  if (hasConformityStandards) {
    return 'IMPLEMENTED_IN_CONFORMITY'
  }

  return 'IMPLEMENTED'
}

export function getRrmImplementationBadgeVariant(
  kind: RrmImplementationBadgeKind
): NonNullable<VariantProps<typeof badgeVariants>['variant']> {
  if (kind === 'IMPLEMENTED' || kind === 'IMPLEMENTED_IN_CONFORMITY') {
    return 'success'
  }
  if (kind === 'IMPLEMENTED_NO_EVIDENCE') {
    return 'warning'
  }
  return 'outline'
}

export function getRrmImplementationBadgeLabelKey(
  kind: RrmImplementationBadgeKind
): string {
  if (kind === 'IMPLEMENTED_IN_CONFORMITY') {
    return 'riskReductionMeasuresLibraryPage.implementation.badges.implementedInConformity'
  }
  if (kind === 'IMPLEMENTED') {
    return 'riskReductionMeasuresLibraryPage.implementation.badges.implemented'
  }
  if (kind === 'IMPLEMENTED_NO_EVIDENCE') {
    return 'riskReductionMeasuresLibraryPage.implementation.badges.implementedNoEvidence'
  }
  return 'riskReductionMeasuresLibraryPage.implementation.badges.notImplemented'
}
